import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { RequireAuth, RequirePermission } from './features/auth';
import { RequireModule } from './features/license/RequireModule';
import { AppLayout } from './layouts/AppLayout';
import { Spinner } from './shared/ui/Spinner';

const LoginView = lazy(async () => ({ default: (await import('./features/auth')).LoginView }));
const DashboardOperationalView = lazy(async () => ({ default: (await import('./features/dashboards')).DashboardOperationalView }));
const DashboardFinancialView = lazy(async () => ({ default: (await import('./features/dashboards')).DashboardFinancialView }));
const ProfileView = lazy(async () => ({ default: (await import('./features/profile')).ProfileView }));
const NotFoundView = lazy(async () => ({ default: (await import('./features/NotFoundView')).NotFoundView }));
const UsersView = lazy(async () => ({ default: (await import('./features/users')).UsersView }));
const RolesView = lazy(async () => ({ default: (await import('./features/roles')).RolesView }));
const SettingsView = lazy(async () => ({ default: (await import('./features/settings')).SettingsView }));
const SetupView = lazy(async () => ({ default: (await import('./features/setup')).SetupView }));
const ExamsView = lazy(async () => ({ default: (await import('./features/exams')).ExamsView }));
const ProfessionalsView = lazy(async () => ({ default: (await import('./features/professionals')).ProfessionalsView }));
const PatientsView = lazy(async () => ({ default: (await import('./features/patients')).PatientsView }));
const ArchivedPatientsView = lazy(async () => ({ default: (await import('./features/patients')).ArchivedPatientsView }));
const PatientProfileView = lazy(async () => ({ default: (await import('./features/patients')).PatientProfileView }));
const ScheduleView = lazy(async () => ({ default: (await import('./features/scheduling')).ScheduleView }));
const MyScheduleView = lazy(async () => ({ default: (await import('./features/scheduling')).MyScheduleView }));
const ScheduleRulesView = lazy(async () => ({ default: (await import('./features/scheduling')).ScheduleRulesView }));
const ScheduleBlocksView = lazy(async () => ({ default: (await import('./features/scheduling')).ScheduleBlocksView }));
const ScheduleHolidaysView = lazy(async () => ({ default: (await import('./features/scheduling')).ScheduleHolidaysView }));
const WhatsappView = lazy(async () => ({ default: (await import('./features/whatsapp')).WhatsappView }));
const AutomationsView = lazy(async () => ({ default: (await import('./features/automations')).AutomationsView }));
const AutomationLogsView = lazy(async () => ({ default: (await import('./features/automations')).AutomationLogsView }));
const NotificationsView = lazy(async () => ({ default: (await import('./features/notifications')).NotificationsView }));
const AuditLogsView = lazy(async () => ({ default: (await import('./features/audit')).AuditLogsView }));
const BotSandboxView = lazy(async () => ({
  default: (await import('./features/settings/components/BotSandboxView')).BotSandboxView,
}));
const ReportsView = lazy(async () => ({ default: (await import('./features/reports')).ReportsView }));
const DailyAgendaReportView = lazy(async () => ({ default: (await import('./features/reports')).DailyAgendaReportView }));
const LaudoEditorView = lazy(async () => ({ default: (await import('./features/laudos')).LaudoEditorView }));
const LaudosWorklistView = lazy(async () => ({ default: (await import('./features/laudos')).WorklistView }));
const ConsentsWorklistView = lazy(async () => ({
  default: (await import('./features/consents/ConsentsWorklistView')).ConsentsWorklistView,
}));
const SupportView = lazy(async () => ({ default: (await import('./features/support')).SupportView }));
const SupportTicketView = lazy(async () => ({ default: (await import('./features/support')).SupportTicketView }));
const WaitlistView = lazy(async () => ({ default: (await import('./features/waitlist/WaitlistView')).default }));
const FinanceCaixaView = lazy(async () => ({
  default: (await import('./features/finance/FinanceCaixaView')).FinanceCaixaView,
}));
const AReceberView = lazy(async () => ({
  default: (await import('./features/finance/receber/AReceberView')).AReceberView,
}));
const ContasView = lazy(async () => ({
  default: (await import('./features/finance/contas/ContasView')).ContasView,
}));
const APagarView = lazy(async () => ({
  default: (await import('./features/finance/pagar/APagarView')).APagarView,
}));
const FornecedoresView = lazy(async () => ({
  default: (await import('./features/finance/fornecedores/FornecedoresView')).FornecedoresView,
}));
const SubscriptionView = lazy(async () => ({
  default: (await import('./features/gaius/SubscriptionView')).SubscriptionView,
}));
const InvoicesView = lazy(async () => ({
  default: (await import('./features/gaius/InvoicesView')).InvoicesView,
}));
const RepasseView = lazy(async () => ({
  default: (await import('./features/finance/repasse/RepasseView')).RepasseView,
}));

function FullScreenLoader() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', background: 'var(--color-background)' }}>
      <Spinner size="lg" />
    </div>
  );
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<FullScreenLoader />}>{node}</Suspense>;
}

const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginView />) },
  {
    element: <RequireAuth />,
    children: [
      // /setup é autenticado mas SEM AppLayout (UI dedicada)
      { path: '/setup', element: withSuspense(<SetupView />) },
      {
        element: <AppLayout />,
        children: [
          { index: true, element: withSuspense(<DashboardOperationalView />) },
          { path: '/profile', element: withSuspense(<ProfileView />) },
          { path: '/notifications', element: withSuspense(<NotificationsView />) },
          {
            element: <RequirePermission permission="MANAGE_USERS" />,
            children: [{ path: '/users', element: withSuspense(<UsersView />) }],
          },
          {
            element: <RequirePermission permission="MANAGE_ROLES" />,
            children: [{ path: '/roles', element: withSuspense(<RolesView />) }],
          },
          {
            element: <RequirePermission permission="MANAGE_SYSTEM_SETTINGS" />,
            children: [
              { path: '/settings', element: withSuspense(<SettingsView />) },
              { path: '/assinatura', element: withSuspense(<SubscriptionView />) },
              { path: '/faturas', element: withSuspense(<InvoicesView />) },
            ],
          },
          {
            element: <RequireModule module="chatbot" />,
            children: [
              {
                element: <RequirePermission permission="MANAGE_CHATBOT" />,
                children: [
                  { path: '/settings/chatbot/sandbox', element: withSuspense(<BotSandboxView />) },
                ],
              },
            ],
          },
          {
            element: <RequirePermission permission="VIEW_EXAMS" />,
            children: [{ path: '/exams', element: withSuspense(<ExamsView />) }],
          },
          {
            element: <RequirePermission permission="MANAGE_PROFESSIONALS" />,
            children: [{ path: '/professionals', element: withSuspense(<ProfessionalsView />) }],
          },
          {
            element: <RequirePermission permission="VIEW_PATIENTS" />,
            children: [
              { path: '/patients', element: withSuspense(<PatientsView />) },
              { path: '/patients/:id', element: withSuspense(<PatientProfileView />) },
            ],
          },
          {
            element: <RequirePermission permission="MANAGE_PATIENTS" />,
            children: [
              { path: '/patients/arquivados', element: withSuspense(<ArchivedPatientsView />) },
            ],
          },
          {
            element: <RequireModule module="agenda" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_SCHEDULE" />,
                children: [
                  { path: '/schedule', element: withSuspense(<ScheduleView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="agenda" />,
            children: [
              {
                element: <RequirePermission check={(p) => p.hasPermission('VIEW_SCHEDULE') || p.isOwnScheduleOnly} />,
                children: [
                  { path: '/my-schedule', element: withSuspense(<MyScheduleView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="agenda" />,
            children: [
              {
                element: <RequirePermission permission="MANAGE_SCHEDULE" />,
                children: [
                  { path: '/schedule/rules', element: withSuspense(<ScheduleRulesView />) },
                  { path: '/schedule/blocks', element: withSuspense(<ScheduleBlocksView />) },
                  { path: '/schedule/holidays', element: withSuspense(<ScheduleHolidaysView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="agenda" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_SCHEDULE" />,
                children: [
                  { path: '/reports', element: withSuspense(<ReportsView />) },
                  { path: '/reports/agenda-diaria', element: withSuspense(<DailyAgendaReportView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="whatsapp" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_WHATSAPP" />,
                children: [{ path: '/whatsapp', element: withSuspense(<WhatsappView />) }],
              },
            ],
          },
          {
            element: <RequireModule module="financeiro" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_FINANCIAL" />,
                children: [
                  { path: '/dashboards/financial', element: withSuspense(<DashboardFinancialView />) },
                  { path: '/financeiro/caixa', element: withSuspense(<FinanceCaixaView />) },
                  { path: '/financeiro/receber', element: withSuspense(<AReceberView />) },
                  { path: '/financeiro/contas', element: withSuspense(<ContasView />) },
                  { path: '/financeiro/pagar', element: withSuspense(<APagarView />) },
                  { path: '/financeiro/repasse', element: withSuspense(<RepasseView />) },
                  { path: '/financeiro/fornecedores', element: withSuspense(<FornecedoresView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="whatsapp" />,
            children: [
              {
                element: <RequirePermission permission="MANAGE_AUTOMATIONS" />,
                children: [
                  { path: '/automations', element: withSuspense(<AutomationsView />) },
                  { path: '/automations/logs', element: withSuspense(<AutomationLogsView />) },
                ],
              },
            ],
          },
          {
            element: <RequirePermission permission="VIEW_AUDIT_LOGS" />,
            children: [{ path: '/audit', element: withSuspense(<AuditLogsView />) }],
          },
          {
            element: <RequireModule module="laudos" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_LAUDO" />,
                children: [
                  { path: '/laudos', element: withSuspense(<LaudosWorklistView />) },
                  { path: '/laudos/:id', element: withSuspense(<LaudoEditorView />) },
                ],
              },
            ],
          },
          {
            element: <RequireModule module="whatsapp" />,
            children: [
              {
                element: <RequirePermission permission="VIEW_PATIENTS" />,
                children: [{ path: '/termos', element: withSuspense(<ConsentsWorklistView />) }],
              },
            ],
          },
          {
            element: <RequirePermission check={(p) => p.hasAnyPermission(['OPEN_SUPPORT_TICKET', 'MANAGE_SUPPORT_TICKETS'])} />,
            children: [
              { path: '/support', element: withSuspense(<SupportView />) },
              { path: '/support/:id', element: withSuspense(<SupportTicketView />) },
            ],
          },
          {
            element: <RequireModule module="lista_espera" />,
            children: [
              {
                element: <RequirePermission permission="MANAGE_WAITLIST" />,
                children: [{ path: '/waitlist', element: withSuspense(<WaitlistView />) }],
              },
            ],
          },
          { path: '*', element: withSuspense(<NotFoundView />) },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
